import { useState } from "react";
import { Navigation, Loader2, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import PlaceCard from "../PlaceCard";

export default function AroundMeSection() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [located, setLocated] = useState(false);

  const locate = () => {
    if (!navigator.geolocation) {
      setError("Géolocalisation indisponible");
      return;
    }
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const { data } = await api.get("/places/nearby", {
            params: { lat: latitude, lng: longitude, radius_km: 25, limit: 8 },
          });
          setPlaces(data);
          setLocated(true);
        } catch {
          setError("Erreur de chargement");
        } finally {
          setLoading(false);
        }
      },
      () => {
        setLoading(false);
        // Fallback: use Saint-Denis center
        api.get("/places/nearby", { params: { lat: -20.8823, lng: 55.4504, radius_km: 25, limit: 8 } })
          .then(r => { setPlaces(r.data); setLocated(true); setError("Position refusée — affichage Saint-Denis"); });
      },
      { timeout: 8000 }
    );
  };

  return (
    <section className="px-4 sm:px-6 max-w-7xl mx-auto" data-testid="around-me-section">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="uppercase-label text-[#2A9D8F] flex items-center gap-1.5"><MapPin size={12}/> Géoloc</div>
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter text-[#1A1A1A] mt-1">
            Autour de moi
          </h2>
        </div>
        {!located && (
          <Button data-testid="locate-btn" onClick={locate} disabled={loading}
            className="rounded-full bg-[#2A9D8F] hover:bg-[#22867A] text-white font-semibold shadow-md">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <Navigation size={14}/>}
            <span className="ml-2">{loading ? "Localisation…" : "Me localiser"}</span>
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-[#FF6B35] mb-4">{error}</p>}
      {!located && !loading && (
        <div className="rounded-3xl bg-gradient-to-br from-[#2A9D8F]/10 via-[#00A6B3]/5 to-transparent border border-[#2A9D8F]/20 p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#2A9D8F] mx-auto flex items-center justify-center text-white mb-3 shadow-md">
            <Navigation size={22}/>
          </div>
          <h3 className="font-display font-bold text-lg">Découvre les spots près de toi</h3>
          <p className="text-sm text-[#1A1A1A]/60 mt-1 max-w-md mx-auto">Active la géolocalisation pour voir les meilleures adresses dans ton rayon.</p>
        </div>
      )}
      {located && places.length > 0 && (
        <div className="flex gap-4 overflow-x-auto no-scrollbar -mx-4 sm:-mx-6 px-4 sm:px-6 pb-2 snap-x snap-mandatory">
          {places.map((p, i) => (
            <div key={p.id} className="snap-start shrink-0 w-[260px] sm:w-[280px]">
              <PlaceCard place={p} index={i} compact />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
